<template>
  <div
    v-if="show"
    class="badiu-tiptap-modal"
    tabindex="-1"
    @mousedown.self="onCancel"
  >
    <div class="badiu-tiptap-modal-dialog badiu-tiptap-modal-dialog-lg">
      <div class="badiu-tiptap-modal-content">

        <div class="badiu-tiptap-modal-header">
          <h6 class="badiu-tiptap-modal-title">
            <i class="fas fa-code"></i>Source code
          </h6>
          <button
            type="button"
            class="badiu-tiptap-modal-close"
            aria-label="Close"
            @click="onCancel"
          >&times;</button>
        </div>

        <div class="badiu-tiptap-modal-body badiu-tiptap-modal-body-flush">
          <textarea
            v-model="code"
            class="badiu-source-textarea"
            spellcheck="false"
          ></textarea>
        </div>

        <div class="badiu-tiptap-modal-footer">
          <button
            type="button"
            class="badiu-tiptap-btn badiu-tiptap-btn-secondary"
            @click="onCancel"
          >
            Cancel
          </button>

          <button
            type="button"
            class="badiu-tiptap-btn badiu-tiptap-btn-primary"
            @click="onSave"
          >
            Save
          </button>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
/**
 * BadiuTiptapSourceCode
 *
 * Isolated modal for viewing and editing raw HTML source code
 * (equivalent to TinyMCE's "code" plugin).
 */
import { ref, watch } from 'vue'

export default {
  name: "BadiuTiptapSourceCode",

  props: {
    show: {
      type: Boolean,
      default: false
    },

    html: {
      type: String,
      default: ""
    }
  },

  emits: ["save", "cancel"],

  setup(props, { emit }) {
    const code = ref("");

    watch(
      () => props.show,
      (visible) => {
        if (visible) {
          code.value = props.html || "";
        }
      },
      { immediate: true }
    );

    function onSave() {
      emit("save", code.value);
    }

    function onCancel() {
      emit("cancel");
    }

    return { code, onSave, onCancel };
  }
};
</script>

<style>
/* ============================================================
 * Modal escopado — não depende de CSS Bootstrap do host.
 * Compartilhado com os outros modais (image/link) via classes
 * .badiu-tiptap-modal-*.
 * ============================================================ */
.badiu-tiptap-modal {
  position: fixed;
  inset: 0;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  z-index: 100000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background-color: rgba(0, 0, 0, 0.5);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  box-sizing: border-box;
  overflow: auto;
}

.badiu-tiptap-modal *,
.badiu-tiptap-modal *::before,
.badiu-tiptap-modal *::after {
  box-sizing: border-box;
}

.badiu-tiptap-modal-dialog {
  position: relative;
  width: 100%;
  max-width: 500px;
  margin: auto;
}

.badiu-tiptap-modal-dialog-lg {
  max-width: 800px;
}

.badiu-tiptap-modal-content {
  display: flex;
  flex-direction: column;
  background-color: #fff;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 0.5rem;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
  overflow: hidden;
  max-height: calc(100vh - 2rem);
}

.badiu-tiptap-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 1rem;
  border-bottom: 1px solid #dee2e6;
  background: #f8f9fa;
}

.badiu-tiptap-modal-title {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #212529;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.badiu-tiptap-modal-title i {
  color: #6c757d;
}

.badiu-tiptap-modal-close {
  background: transparent;
  border: 0;
  font-size: 1.5rem;
  line-height: 1;
  color: #6c757d;
  cursor: pointer;
  padding: 0 0.25rem;
}

.badiu-tiptap-modal-close:hover {
  color: #000;
}

.badiu-tiptap-modal-body {
  padding: 1rem;
  overflow: auto;
  flex: 1 1 auto;
  min-height: 0;
}

.badiu-tiptap-modal-body-flush {
  padding: 0;
}

.badiu-tiptap-modal-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-top: 1px solid #dee2e6;
  background: #f8f9fa;
}

.badiu-tiptap-btn {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.5;
  text-align: center;
  vertical-align: middle;
  cursor: pointer;
  user-select: none;
  border: 1px solid transparent;
  border-radius: 0.25rem;
  background-color: #fff;
  color: #212529;
}

.badiu-tiptap-btn-primary {
  color: #fff;
  background-color: #0d6efd;
  border-color: #0d6efd;
}

.badiu-tiptap-btn-primary:hover {
  background-color: #0b5ed7;
  border-color: #0a58ca;
}

.badiu-tiptap-btn-secondary {
  color: #fff;
  background-color: #6c757d;
  border-color: #6c757d;
}

.badiu-tiptap-btn-secondary:hover {
  background-color: #5c636a;
  border-color: #565e64;
}

.badiu-source-textarea {
  display: block;
  width: 100%;
  height: 420px;
  min-height: 300px;
  padding: 0.75rem 1rem;
  font-family: SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  font-size: 13px;
  line-height: 1.5;
  color: #212529;
  background-color: #fff;
  border: 0;
  border-radius: 0;
  resize: vertical;
  outline: none;
  box-sizing: border-box;
}
</style>
